import { withStyles } from "@material-ui/core/styles";
import MenuIcon from "@material-ui/icons/Menu";
import { UISref, useCurrentStateAndParams, useRouter } from "@uirouter/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { OverlayRoutes } from "../../pages/overlay/routes";
import { ProfileRoutes } from "../../pages/profile/routes";
import { AuthActions } from "../../store/auth/actions";
import { useIsImpersonation } from "../../store/auth/hooks";
import { SidebarActions } from "../../store/sidebar/actions";
import { colours } from "../../styles/colours";
import { Button } from "../button";
import { Icon } from "../icons";
import { Flex, hiddenDesktop, hiddenMobile } from "../layout";
import { Permission } from "../permission";
import { Avatar } from "./avatar";
import { CreditBalance } from "./credit-balance";
import { WalletBalance } from "./wallet-balance";

const Container = styled(Flex)`
  padding: 1em;
  background: ${colours.white};
  justify-content: space-between;
  border-left: 2px solid ${colours.darkWhite};
`;

const CompanyName = styled.h3`
  ${hiddenDesktop};
  margin: 0;
  font-size: 0.8em;
  color: ${colours.darkGrey};
`;

const Hamburger = styled(
  withStyles({
    root: {
      height: "3em",
      width: "3em",
      margin: "0.5em",
      padding: "0.5em",
    },
  })(MenuIcon)
)`
  ${hiddenDesktop}
`;

const PageTitle = styled.h2`
  font-size: 1.7em;
  margin: 0;
  font-family: "Patua One", sans-serif;
`;

namespace Logout {
  const CTA = styled(Button)`
    cursor: pointer;
    font-weight: 700;
  `;

  const ItemIcon = styled(Icon).attrs({ type: "logout" })`
    margin-left: 0.5em;
  `;

  const DesktopCTA = styled(CTA)`
    ${hiddenMobile}
  `;

  export const LogoutItem = () => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    return (
      <DesktopCTA variant="text" onClick={() => dispatch(AuthActions.logout())}>
        {t("logout")} <ItemIcon />
      </DesktopCTA>
    );
  };

  export const StopImpersonationItem = () => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    return (
      <DesktopCTA
        variant="contained"
        colour="danger"
        onClick={() => dispatch(AuthActions.stopImpersonationRequest())}
      >
        {t("stop-impersonation")}
      </DesktopCTA>
    );
  };
}

const ProfileLink = styled(UISref).attrs({ to: ProfileRoutes.name })`
  ${hiddenMobile}
`;

const usePageTitle = () => {
  // State for title
  const [title, setTitle] = React.useState("");
  const { t } = useTranslation("page-titles");

  const router = useRouter();
  useCurrentStateAndParams();
  useEffect(() => {
    document.title =
      "Sekuritance" +
      (router.globals.current.data
        ? ` | ${t(router.globals.current.data.title)}`
        : "");

    const name: string = router.globals.current.name || "";
    if (!name.split(".").includes(OverlayRoutes.name)) {
      setTitle(t(router.globals.current.data.title));
    }
  }, [router.globals.current.data]);

  return title;
};

const StyledCreditBalance = styled(CreditBalance)`
  ${hiddenMobile};
  margin-left: 0.5em;
`;

export const Header = () => {
  const dispatch = useDispatch();
  const pageTitle = usePageTitle();
  const { t } = useTranslation("common");
  const isImpersonation = useIsImpersonation();

  return (
    <Container>
      <Flex>
        <Hamburger onClick={() => dispatch(SidebarActions.openSidebar())} />
        <div>
          <CompanyName>Sekuritance</CompanyName>
          <PageTitle className="h2">{pageTitle || t("welcome")}</PageTitle>
        </div>
      </Flex>
      <Flex center>
        <StyledCreditBalance />
        <Permission roles={["ROLE_CRYPTO"]}>
          <WalletBalance />
        </Permission>
        {isImpersonation ? <Logout.StopImpersonationItem /> : null}
        <Logout.LogoutItem />
        <Permission
          roles={["ROLE_ADMIN", "ROLE_COMMUNITY_MANAGER"]}
          alternative={
            <ProfileLink>
              <Button variant="text">
                <Avatar />
              </Button>
            </ProfileLink>
          }
        >
          <Avatar />
        </Permission>
      </Flex>
    </Container>
  );
};
